import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { logout, selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { ValidationModal } from '@/components/ui/ValidationModal'
import { SessionActivityDialog } from '@/components/auth/SessionActivityDialog'
import { logout as logoutApi } from '@/lib/api/authService'
import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import {
  beginIntentionalSessionEnd,
  currentLocationForRedirect,
  getActivityTime,
  getLastApiRequestTime,
  isIntentionalSessionEnd,
  setActivityTime,
  setLastApiRequestTime,
  setPostLoginRedirect,
} from '@/lib/auth/sessionActivity'
import { loadSessionTimeoutConfig } from '@/lib/auth/loadSessionTimeout'
import {
  BEACON_IDLE_API_MS,
  defaultSessionTimeout,
  EXPIRED_SESSION_MESSAGE,
  OTHER_TAB_SESSION_MESSAGE,
  TOKEN_POLL_INTERVAL_MS,
  warningRemainingSeconds,
  type SessionDialog,
  type SessionTimeoutConfig,
} from '@/lib/auth/sessionTimeoutConfig'

function readSelectedRoleName(): string | undefined {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return undefined
    const user = JSON.parse(raw) as { current_role?: { role_name?: string } }
    return user.current_role?.role_name
  } catch {
    return undefined
  }
}

function isImpersonateProcessActive(): boolean {
  try {
    return (
      sessionStorage.getItem('impersonate_process') === '1' ||
      sessionStorage.getItem('impersonateprocess') === '1'
    )
  } catch {
    return false
  }
}

export function SessionTimeoutManager() {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)
  const roleName = user?.current_role?.role_name

  const [dialog, setDialog] = useState<SessionDialog>('none')
  const [config, setConfig] = useState<SessionTimeoutConfig>(defaultSessionTimeout)

  const warningIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const expireTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tokenPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const beaconInFlightRef = useRef(false)
  const loggingOutRef = useRef(false)
  const trackedRoleNameRef = useRef<string | undefined>(undefined)
  const configRef = useRef(config)
  configRef.current = config

  const clearWarningInterval = useCallback(() => {
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current)
      warningIntervalRef.current = null
    }
  }, [])

  const clearExpireTimeout = useCallback(() => {
    if (expireTimeoutRef.current) {
      clearTimeout(expireTimeoutRef.current)
      expireTimeoutRef.current = null
    }
  }, [])

  const startExpireTimeout = useCallback(() => {
    clearExpireTimeout()
    const { sessionMs, warningMs } = configRef.current
    expireTimeoutRef.current = setTimeout(() => {
      if (Date.now() - getActivityTime() < sessionMs) return
      clearWarningInterval()
      setDialog('expired')
      setPostLoginRedirect(currentLocationForRedirect())
      try {
        sessionStorage.setItem('session_expired_notice', EXPIRED_SESSION_MESSAGE)
      } catch {
        /* ignore */
      }

      if (loggingOutRef.current) return
      loggingOutRef.current = true
      beginIntentionalSessionEnd()
      void logoutApi().finally(() => {
        dispatch(logout())
      })
    }, sessionMs - warningMs)
  }, [clearExpireTimeout, clearWarningInterval, dispatch])

  const startWarningTimer = useCallback(() => {
    clearWarningInterval()
    const { warningMs } = configRef.current
    warningIntervalRef.current = setInterval(() => {
      if (Date.now() - getActivityTime() < warningMs) return
      clearWarningInterval()
      setDialog('warning')
      startExpireTimeout()
    }, warningMs)
  }, [clearWarningInterval, startExpireTimeout])

  const resetActivity = useCallback(() => {
    if (loggingOutRef.current) return
    setActivityTime(Date.now())
    clearExpireTimeout()
    if (dialog === 'warning') setDialog('none')
    startWarningTimer()
  }, [clearExpireTimeout, dialog, startWarningTimer])

  const pingBeaconIfNeeded = useCallback(() => {
    if (beaconInFlightRef.current) return
    if (Date.now() - getLastApiRequestTime() <= BEACON_IDLE_API_MS) return
    beaconInFlightRef.current = true
    setLastApiRequestTime(Date.now())
    void apiClient
      .get(API_ENDPOINTS.BEACON)
      .catch(() => {
        /* non-critical */
      })
      .finally(() => {
        beaconInFlightRef.current = false
      })
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !roleName) return

    let cancelled = false
    trackedRoleNameRef.current = roleName

    void loadSessionTimeoutConfig(roleName).then((next) => {
      if (cancelled) return
      setConfig(next)
      setActivityTime(Date.now())
    })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, roleName])

  useEffect(() => {
    if (!isAuthenticated || !roleName) {
      clearWarningInterval()
      clearExpireTimeout()
      setDialog((current) =>
        current === 'expired' || current === 'other-tab' ? current : 'none',
      )
      return
    }

    setActivityTime(Date.now())
    clearExpireTimeout()
    startWarningTimer()
    return () => {
      clearWarningInterval()
      clearExpireTimeout()
    }
  }, [
    isAuthenticated,
    roleName,
    config.sessionMs,
    config.warningMs,
    clearExpireTimeout,
    clearWarningInterval,
    startWarningTimer,
  ])

  useEffect(() => {
    if (!isAuthenticated || !roleName) return

    const onActivity = () => {
      const { warningMs } = configRef.current
      if (Date.now() - getActivityTime() >= warningMs) return
      setActivityTime(Date.now())
      startWarningTimer()
      pingBeaconIfNeeded()
    }

    document.addEventListener('keyup', onActivity)
    document.addEventListener('click', onActivity)
    return () => {
      document.removeEventListener('keyup', onActivity)
      document.removeEventListener('click', onActivity)
    }
  }, [isAuthenticated, roleName, startWarningTimer, pingBeaconIfNeeded])

  useEffect(() => {
    const showOtherTabIfNeeded = () => {
      if (isIntentionalSessionEnd() || loggingOutRef.current) return
      if (!getAuthTokenFromStorage() && trackedRoleNameRef.current) {
        setDialog('other-tab')
      }
    }

    const onStorage = (event: StorageEvent) => {
      if (
        event.storageArea !== localStorage ||
        (event.key !== 'token' && event.key !== 'authToken')
      ) {
        return
      }
      if (!event.newValue) showOtherTabIfNeeded()
    }

    window.addEventListener('storage', onStorage)

    if (!isAuthenticated) {
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current)
        tokenPollRef.current = null
      }
      return () => window.removeEventListener('storage', onStorage)
    }

    tokenPollRef.current = setInterval(() => {
      if (!getAuthTokenFromStorage()) {
        showOtherTabIfNeeded()
        return
      }

      const otherTabRole = readSelectedRoleName()
      const tracked = trackedRoleNameRef.current
      if (
        tracked &&
        otherTabRole &&
        tracked !== otherTabRole &&
        !isImpersonateProcessActive()
      ) {
        window.location.reload()
      }
    }, TOKEN_POLL_INTERVAL_MS)

    return () => {
      window.removeEventListener('storage', onStorage)
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current)
        tokenPollRef.current = null
      }
    }
  }, [isAuthenticated])

  const handleContinueWarning = () => {
    clearExpireTimeout()
    setDialog('none')
    resetActivity()
  }

  const handleDismissWarning = () => {
    setDialog('none')
  }

  const handleExpiredOk = () => {
    setDialog('none')
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
  }

  const handleOtherTabContinue = () => {
    // Keep this tab's page if the reload has to go through /login / role selection.
    setPostLoginRedirect(currentLocationForRedirect())
    window.location.reload()
  }

  const handleOtherTabCancel = () => {
    setDialog('none')
  }

  const warningSeconds = warningRemainingSeconds(config.sessionMs, config.warningMs)
  const warningMessage = `Due to Inactivity your session going to expire after ${warningSeconds} Second, Click Continue to Extend Session`

  return (
    <>
      <SessionActivityDialog
        isOpen={dialog === 'warning'}
        title="Session Activity"
        message={warningMessage}
        primaryLabel="Continue"
        secondaryLabel="Cancel"
        onPrimary={handleContinueWarning}
        onSecondary={handleDismissWarning}
        onDismiss={handleDismissWarning}
        primaryTestId="session-warning-continue"
        secondaryTestId="session-warning-cancel"
        overlayTestId="session-warning-overlay"
      />
      <ValidationModal
        isOpen={dialog === 'expired'}
        title="Session Expired"
        message={EXPIRED_SESSION_MESSAGE}
        confirmText="Ok"
        showValidationIcon={false}
        alignMessage
        okButtonStyle="saveChanges"
        onClose={handleExpiredOk}
      />
      <SessionActivityDialog
        isOpen={dialog === 'other-tab'}
        title="Session Activity"
        message={OTHER_TAB_SESSION_MESSAGE}
        primaryLabel="Continue"
        secondaryLabel="Cancel"
        onPrimary={handleOtherTabContinue}
        onSecondary={handleOtherTabCancel}
        onDismiss={handleOtherTabCancel}
        primaryTestId="session-other-tab-continue"
        secondaryTestId="session-other-tab-cancel"
        overlayTestId="session-other-tab-overlay"
      />
    </>
  )
}
